import {produce} from 'immer';

import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessFlowchartValidationError} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {
  FlowchartStaticEdgeData,
  FlowchartEdge,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {EdgeType, ElkEdgeData} from 'ui-library/reactflow/edges/edgeOptions';

import {
  StaticFlowchartNodeData,
  FlowchartNode,
  isStaticTaskSchemaNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {createInteractiveNodeFromStaticData, FlowchartOptions} from './processFlowchartConfigEditorUtils';

export interface FlowchartData {
  nodes: Array<FlowchartNode>;
  edges: Array<FlowchartEdge>;
}

interface BuildInteractiveFlowchartFromStaticDataRunnerArgs {
  staticNodesData: Array<StaticFlowchartNodeData>;
  staticEdgesData: Array<FlowchartStaticEdgeData>;
  options: FlowchartOptions;
  nodePositionsByNodeId?: Record<string, {x: number; y: number}>;
  selectedNodeIds?: Set<string>;
  selectedRowIds?: Set<string>;
  selectedEdgeIds?: Set<string>;
  elkEdgeDataByEdgeId?: Record<string, ElkEdgeData>;
  validationErrors?: Array<ProcessFlowchartValidationError>;
}

class BuildInteractiveFlowchartFromStaticDataRunner {
  private readonly staticNodesData: Array<StaticFlowchartNodeData>;
  private readonly staticEdgesData: Array<FlowchartStaticEdgeData>;
  private readonly options: FlowchartOptions;
  private readonly nodePositionsByNodeId: Record<string, {x: number; y: number}>;
  private readonly selectedNodeIds: Set<string>;
  private readonly selectedRowIds: Set<string>;
  private readonly selectedEdgeIds: Set<string>;
  private readonly elkEdgeDataByEdgeId: Record<string, ElkEdgeData>;
  private readonly validationErrors: Array<ProcessFlowchartValidationError>;
  private nodes: Array<FlowchartNode>;
  private edges: Array<FlowchartEdge>;

  constructor(args: BuildInteractiveFlowchartFromStaticDataRunnerArgs) {
    this.staticNodesData = args.staticNodesData;
    this.staticEdgesData = args.staticEdgesData;
    this.options = args.options;
    this.nodePositionsByNodeId = args.nodePositionsByNodeId || {};
    this.selectedNodeIds = args.selectedNodeIds || new Set();
    this.selectedRowIds = args.selectedRowIds || new Set();
    this.selectedEdgeIds = args.selectedEdgeIds || new Set();
    this.elkEdgeDataByEdgeId = args.elkEdgeDataByEdgeId || {};
    this.validationErrors = args.validationErrors || [];
    this.nodes = [];
    this.edges = [];
  }

  run(): FlowchartData {
    this.convertStaticDataToNodes();
    this.convertStaticDataToEdges();
    return {
      nodes: this.nodes,
      edges: this.edges,
    };
  }

  private convertStaticDataToNodes(): void {
    // Sort nodes so that the root node is first and the output node is last
    const rootNode = this.staticNodesData.find(
      (staticNodeData) =>
        isStaticTaskSchemaNodeData(staticNodeData) &&
        staticNodeData.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    );
    const outputNode = this.staticNodesData.find(
      (staticNodeData) =>
        isStaticTaskSchemaNodeData(staticNodeData) &&
        staticNodeData.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
    );

    // It is not expected that the flowchart be empty since we should always have at least a root node.
    // So we can remove this check once we have validated that there are no real cases of an empty model.
    if (this.staticNodesData.length === 0) {
      this.nodes = [];
      return;
    }

    if (!rootNode) {
      throw new Error('No root node found');
    }

    const nodesToExclude = [rootNode.id];
    if (outputNode) {
      nodesToExclude.push(outputNode.id);
    }

    const taskNodes = this.staticNodesData.filter(
      (staticNodeData) => !nodesToExclude.includes(staticNodeData.id)
    );

    const orderedNodes = [rootNode, ...taskNodes];
    if (outputNode) {
      orderedNodes.push(outputNode);
    }

    this.nodes = orderedNodes.map((staticNodeData) => {
      const position = this.nodePositionsByNodeId[staticNodeData.id] || {x: 0, y: 0};
      const baseNode = createInteractiveNodeFromStaticData(staticNodeData, position, this.options);
      return produce(baseNode, (draft) => {
        draft.selected = this.selectedNodeIds.has(staticNodeData.id);
        if (isStaticTaskSchemaNodeData(draft.data)) {
          draft.data.sections.forEach((section) => {
            section.sectionRows.forEach((row) => {
              row.isSelected = this.selectedRowIds.has(row.id);
            });
          });
        }
        draft.data.validationErrors = this.validationErrors.filter(
          (error) =>
            error.object?.clientId === staticNodeData.id ||
            error.parentObjects?.some((p) => p.clientId === staticNodeData.id)
        );
      });
    });
  }

  private convertStaticDataToEdges(): void {
    this.edges = this.staticEdgesData.map((staticEdgeData) =>
      produce(staticEdgeData as FlowchartEdge, (draft) => {
        draft.selected = this.selectedEdgeIds.has(staticEdgeData.id);
        draft.data = {
          validationErrors: this.validationErrors.filter(
            (error) =>
              error.object?.clientId === staticEdgeData.id ||
              error.parentObjects?.some((p) => p.clientId === staticEdgeData.id)
          ),
          elkEdgeData: this.elkEdgeDataByEdgeId[staticEdgeData.id],
          type: EdgeType.ELK_SPLINE,
        };
      })
    );
  }
}

export default function buildInteractiveFlowchartFromStaticData(
  args: BuildInteractiveFlowchartFromStaticDataRunnerArgs
): FlowchartData {
  const runner = new BuildInteractiveFlowchartFromStaticDataRunner(args);
  return runner.run();
}
